<template>
  <div class="driver-actuals-table-wrap">
    <table class="driver-actuals-table" width="100%" cellpadding="0" cellspacing="0">
      <thead>
        <tr>
          <th colspan="1">&nbsp;</th>
          <th
            v-for="day in timelineDays"
            :key="'hdr-' + day"
            class="driver-actuals-hours-header"
            :colspan="timelineSlotsPerDay"
          >{{ day }}</th>
        </tr>
        <tr>
          <th width="120px">{{ resourceColumnLabel }}</th>
          <template v-for="day in timelineDays">
            <th
              v-for="slot in timelineSlots"
              :key="day + '-' + slot.startHour"
              class="driver-actuals-hour-label"
            >{{ slot.label }}</th>
          </template>
        </tr>
      </thead>
      <tbody ref="timelineBody" @mouseover="handleMouseover" @mouseout="handleMouseout">
        <tr
          v-for="row in internalRows"
          :key="row._rowKey"
          :class="{ [continuationRowClass]: row[skipCellField] }"
        >
          <td
            v-if="!row[skipCellField]"
            :rowspan="row[rowspanField]"
            :class="[resourceCellClass, { [resourceCellGroupedClass]: row[rowspanField] > 1 }]"
          >{{ row[labelField] }}</td>
          <template v-for="day in timelineDays">
            <td
              v-for="slot in timelineSlots"
              :key="row._rowKey + '-' + day + '-' + slot.startHour"
              class="driver-actuals-slot-cell"
            >
              <table class="driver-actuals-slot-inner" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td
                    v-for="(slice, sliceIdx) in slot.slices"
                    :key="sliceIdx"
                    class="driver-actuals-util-slice"
                    :class="row._cellClassCache[day + '|' + slice.hour + '|' + slice.minutes] || emptyCellClasses"
                    :style="{ '--slice-index-in-day': sliceIndexInDay(slot, sliceIdx) }"
                    :data-row-key="row._rowKey"
                    :data-day="day"
                    :data-hour="slice.hour"
                    :data-minutes="slice.minutes"
                  >&nbsp;</td>
                </tr>
              </table>
            </td>
          </template>
        </tr>
        <tr v-if="!internalRows.length">
          <td :colspan="1 + timelineHoursColspan" class="text-center text-muted py-4">No bookings found for this search.</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { getCachedCellSegments } from '@/helpers/actuals_timeline_cache'

export default {
  name: 'driver_actuals_timeline_table',

  props: {
    resourceType: {
      type: String,
      default: 'driver',
      validator: value => value === 'driver' || value === 'vehicle',
    },
    rows: { type: Array, default: () => [] },
    timelineDays: { type: Array, default: () => [] },
    timelineSlots: { type: Array, default: () => [] },
    timelineSlotsPerDay: { type: Number, default: 12 },
  },

  data() {
    return {
      internalRows: [],
      rowByKey: Object.create(null),
      activeHoverCellKey: null,
      emptyCellClasses: Object.freeze([]),
    }
  },

  watch: {
    rows: {
      immediate: true,
      handler(rows) {
        this.setRows(rows)
      },
    },
  },

  computed: {
    isVehicle() {
      return this.resourceType === 'vehicle'
    },
    resourceColumnLabel() {
      return this.isVehicle ? 'Vehicle' : 'Driver'
    },
    labelField() {
      return this.isVehicle ? 'vehicle_label' : 'driver_label'
    },
    skipCellField() {
      return this.isVehicle ? 'skip_vehicle_cell' : 'skip_driver_cell'
    },
    rowspanField() {
      return this.isVehicle ? 'vehicle_rowspan' : 'driver_rowspan'
    },
    resourceCellClass() {
      return this.isVehicle ? 'driver-actuals-vehicle-cell' : 'driver-actuals-driver-cell'
    },
    resourceCellGroupedClass() {
      return this.isVehicle ? 'driver-actuals-vehicle-cell--grouped' : 'driver-actuals-driver-cell--grouped'
    },
    continuationRowClass() {
      return this.isVehicle ? 'driver-actuals-vehicle-continuation' : 'driver-actuals-driver-continuation'
    },
    timelineHoursColspan() {
      return this.timelineSlotsPerDay * this.timelineDays.length
    },
  },

  methods: {
    setRows(rows) {
      const rowByKey = Object.create(null)
      const nextRows = (rows || []).map((row, idx) => {
        const key = [row.row_type, row.driver || row.vehicle, row.booking_id, row.start_datetime, idx].join('|')
        row._rowKey = key
        rowByKey[key] = row
        return row
      })
      this.rowByKey = rowByKey
      this.internalRows = nextRows
      this.activeHoverCellKey = null
    },
    cellCacheKey(rowKey, day, hour, minutes) {
      return `${rowKey}|${day}|${hour}|${minutes}`
    },
    sliceIndexInDay(slot, sliceIdx) {
      return (slot.startHour / 2) * 8 + sliceIdx
    },
    handleMouseover(event) {
      const cell = event.target.closest('td.driver-actuals-util-slice[data-row-key]')
      if (!cell)
        return
      const rowKey = cell.dataset.rowKey
      const row = this.rowByKey[rowKey]
      if (!row)
        return
      const day = cell.dataset.day
      const hour = Number(cell.dataset.hour)
      const minutes = Number(cell.dataset.minutes)
      const cellKey = this.cellCacheKey(rowKey, day, hour, minutes)
      if (cellKey === this.activeHoverCellKey)
        return
      this.activeHoverCellKey = cellKey
      const segments = getCachedCellSegments(row, day, hour, minutes)
      this.$emit('cell-hover', { event, segments, cellKey })
    },
    handleMouseout(event) {
      const tbody = this.$refs.timelineBody
      const related = event.relatedTarget
      if (tbody && related && tbody.contains(related))
        return
      this.activeHoverCellKey = null
      this.$emit('cell-leave')
    },
  },
}
</script>

<style scoped>
.driver-actuals-table-wrap {
  /* Matches driver_actuals_filter_chips.vue status chip colors */
  --driver-actuals-ok-bar: hsla(120, 100%, 75%, 0.78);
  --driver-actuals-ok-bar-hover: hsla(120, 100%, 65%, 0.88);
  --driver-actuals-error-bar: rgba(255, 0, 0, 0.38);
  --driver-actuals-error-bar-hover: rgba(255, 0, 0, 0.52);
  --driver-actuals-planned-bar: hsla(200, 40%, 55%, 0.78);
  --driver-actuals-planned-bar-hover: hsla(200, 40%, 45%, 0.88);
  --driver-actuals-overlap-bar: hsla(35, 100%, 70%, 0.82);
  --driver-actuals-overlap-bar-hover: hsla(35, 100%, 60%, 0.92);
  /* Opaque stripe colors — semi-transparent values blend in repeating gradients */
  --driver-actuals-ok-stripe: #6fcf6f;
  --driver-actuals-ok-stripe-hover: #5ec05e;
  --driver-actuals-error-stripe: #e57373;
  --driver-actuals-error-stripe-hover: #ef5350;
  --driver-actuals-planned-stripe: hsl(200, 40%, 55%);
  --driver-actuals-planned-stripe-hover: hsl(200, 40%, 45%);
  --driver-actuals-overlap-stripe: #ffb74d;
  --driver-actuals-overlap-stripe-hover: #ffa726;
  /* Fixed px stripes + slice-index phase = same look on 1/3/7-day views */
  --driver-actuals-stripe-width: 12px;
  overflow-x: auto;
  max-height: 70vh;
}
.driver-actuals-table,
.driver-actuals-table th,
.driver-actuals-table td {
  border: 1px solid rgba(0, 0, 0, 0.5);
}
.driver-actuals-table th {
  position: sticky;
  top: 0;
  background-color: #f9f8f8;
  z-index: 1;
}
.driver-actuals-slot-cell {
  padding: 0;
  vertical-align: middle;
}
.driver-actuals-slot-inner {
  border-collapse: collapse;
}
.driver-actuals-slot-inner td {
  border: none;
}
.driver-actuals-util-slice {
  position: relative;
  min-width: 3px;
  height: 26px;
  padding: 0;
  background: transparent;
  vertical-align: middle;
}
.driver-actuals-bar--ok::before,
.driver-actuals-bar--overlap::before,
.driver-actuals-bar--overlap-ok::before,
.driver-actuals-bar--overlap-planned::before,
.driver-actuals-bar--overlap-error::before,
.driver-actuals-bar--planned::before,
.driver-actuals-bar--error::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 17px;
  transform: translateY(-50%);
  pointer-events: none;
}
.driver-actuals-bar--ok::before {
  background: var(--driver-actuals-ok-bar);
}
.driver-actuals-bar--overlap::before {
  background: var(--driver-actuals-overlap-bar);
}
.driver-actuals-bar--planned::before {
  background: var(--driver-actuals-planned-bar);
}
.driver-actuals-bar--error::before {
  background: var(--driver-actuals-error-bar);
}
.driver-actuals-bar--overlap-ok::before,
.driver-actuals-bar--overlap-planned::before,
.driver-actuals-bar--overlap-error::before {
  background-size: calc(var(--driver-actuals-stripe-width) * 2) 100%;
  background-position: calc(var(--slice-index-in-day, 0) * -100%) 0;
}
.driver-actuals-bar--overlap-ok::before {
  background-image: repeating-linear-gradient(
    to right,
    var(--driver-actuals-ok-stripe) 0,
    var(--driver-actuals-ok-stripe) var(--driver-actuals-stripe-width),
    var(--driver-actuals-overlap-stripe) var(--driver-actuals-stripe-width),
    var(--driver-actuals-overlap-stripe) calc(var(--driver-actuals-stripe-width) * 2)
  );
}
.driver-actuals-bar--overlap-planned::before {
  background-image: repeating-linear-gradient(
    to right,
    var(--driver-actuals-planned-stripe) 0,
    var(--driver-actuals-planned-stripe) var(--driver-actuals-stripe-width),
    var(--driver-actuals-overlap-stripe) var(--driver-actuals-stripe-width),
    var(--driver-actuals-overlap-stripe) calc(var(--driver-actuals-stripe-width) * 2)
  );
}
.driver-actuals-bar--overlap-error::before {
  background-image: repeating-linear-gradient(
    to right,
    var(--driver-actuals-error-stripe) 0,
    var(--driver-actuals-error-stripe) var(--driver-actuals-stripe-width),
    var(--driver-actuals-overlap-stripe) var(--driver-actuals-stripe-width),
    var(--driver-actuals-overlap-stripe) calc(var(--driver-actuals-stripe-width) * 2)
  );
}
.driver-actuals-bar--solo::before {
  left: 2px;
  right: 2px;
  border-radius: 6px;
}
.driver-actuals-bar--start::before {
  left: 2px;
  border-radius: 6px 0 0 6px;
}
.driver-actuals-bar--end::before {
  right: 2px;
  border-radius: 0 6px 6px 0;
}
.driver-actuals-bar--mid::before {
  border-radius: 0;
}
.driver-actuals-bar--gap::after {
  content: '';
  position: absolute;
  left: 3px;
  top: 50%;
  height: 17px;
  transform: translateY(-50%);
  width: 2px;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 1px;
  pointer-events: none;
}
.driver-actuals-util-slice.driver-actuals-bar--ok:hover::before {
  background: var(--driver-actuals-ok-bar-hover);
}
.driver-actuals-util-slice.driver-actuals-bar--overlap:hover::before {
  background: var(--driver-actuals-overlap-bar-hover);
}
.driver-actuals-util-slice.driver-actuals-bar--overlap-ok:hover::before,
.driver-actuals-util-slice.driver-actuals-bar--overlap-planned:hover::before,
.driver-actuals-util-slice.driver-actuals-bar--overlap-error:hover::before {
  background-size: calc(var(--driver-actuals-stripe-width) * 2) 100%;
  background-position: calc(var(--slice-index-in-day, 0) * -100%) 0;
}
.driver-actuals-util-slice.driver-actuals-bar--overlap-ok:hover::before {
  background-image: repeating-linear-gradient(
    to right,
    var(--driver-actuals-ok-stripe-hover) 0,
    var(--driver-actuals-ok-stripe-hover) var(--driver-actuals-stripe-width),
    var(--driver-actuals-overlap-stripe-hover) var(--driver-actuals-stripe-width),
    var(--driver-actuals-overlap-stripe-hover) calc(var(--driver-actuals-stripe-width) * 2)
  );
}
.driver-actuals-util-slice.driver-actuals-bar--overlap-planned:hover::before {
  background-image: repeating-linear-gradient(
    to right,
    var(--driver-actuals-planned-stripe-hover) 0,
    var(--driver-actuals-planned-stripe-hover) var(--driver-actuals-stripe-width),
    var(--driver-actuals-overlap-stripe-hover) var(--driver-actuals-stripe-width),
    var(--driver-actuals-overlap-stripe-hover) calc(var(--driver-actuals-stripe-width) * 2)
  );
}
.driver-actuals-util-slice.driver-actuals-bar--overlap-error:hover::before {
  background-image: repeating-linear-gradient(
    to right,
    var(--driver-actuals-error-stripe-hover) 0,
    var(--driver-actuals-error-stripe-hover) var(--driver-actuals-stripe-width),
    var(--driver-actuals-overlap-stripe-hover) var(--driver-actuals-stripe-width),
    var(--driver-actuals-overlap-stripe-hover) calc(var(--driver-actuals-stripe-width) * 2)
  );
}
.driver-actuals-util-slice.driver-actuals-bar--planned:hover::before {
  background: var(--driver-actuals-planned-bar-hover);
}
.driver-actuals-util-slice.driver-actuals-bar--error:hover::before {
  background: var(--driver-actuals-error-bar-hover);
}
.driver-actuals-hours-header {
  background-color: #ddd;
  text-align: center;
}
.driver-actuals-hour-label {
  background-color: #ddd;
  font-size: 11px;
  font-weight: bold;
  min-width: 14px;
  padding: 2px 0;
}
.driver-actuals-driver-cell,
.driver-actuals-vehicle-cell {
  font-weight: bold;
  vertical-align: middle;
  white-space: nowrap;
}
.driver-actuals-driver-cell--grouped,
.driver-actuals-vehicle-cell--grouped {
  vertical-align: middle;
}
.driver-actuals-driver-continuation td,
.driver-actuals-vehicle-continuation td {
  border-top: none;
}
</style>
